import React, {useMemo} from "react";
import Seo from "@/components/Seo";
import {SEO} from "@/lib/seoConfig";
import {Link} from "react-router-dom";
import {Section, Kicker, SerifTitle} from "@/components/Section";
import {EditorialImage} from "@/components/EditorialImage";
import {Separator} from "@/components/ui/separator";
import {Masonry} from "@/components/Masonry";
import {StickyThemeNav} from "@/components/StickyThemeNav";
import {ATELIER_TIERS} from "@/lib/data";
import {
    useFineArtPrints,
    useTravelArchive,
    useAtelierHero,
    seriesFromCollection,
} from "@/lib/content";

// ─── Editorial series order ─────────────────────────────────────────────
//
// Locked sequence per user sign-off. Anything not in this list (future
// Sanity additions) renders alphabetically at the end so new series
// degrade gracefully without a code change.
const SERIES_ORDER = [
    "Japan Series",
    "Kalmykia",
    "New York Series",
    "Landscapes Without Witness",
    "Architecture in Repose",
    "Mountain Studies",
    "Quiet Human Presence",
];

// slugify — stable React keys + scroll anchors. Lowercase, hyphenated, ASCII.
const slugifySeries = (s) =>
    (s || "")
        .toLowerCase()
        .replace(/[^\p{L}\p{N}]+/gu, "-")
        .replace(/^-+|-+$/g, "");

// Resolve a print's series name. Sanity prints have it as a top-level field;
// data.js fallback prints carry it inside `collection` (Phase 7 behavior).
const resolveSeries = (print) =>
    print?.series || seriesFromCollection(print?.collection) || null;

// Within-section sort: `order` asc, tiebreaker title alphabetical.
const sortWithinSeries = (a, b) => {
    const oa = typeof a.order === "number" ? a.order : Number.POSITIVE_INFINITY;
    const ob = typeof b.order === "number" ? b.order : Number.POSITIVE_INFINITY;
    if (oa !== ob) return oa - ob;
    return (a.title || "").localeCompare(b.title || "");
};

// Group prints into SERIES_ORDER buckets + an "extras" tail for any future
// series Sanity ships that isn't in our canonical list.
const groupBySeries = (prints) => {
    const known = new Map(SERIES_ORDER.map((s) => [s, []]));
    const extras = new Map();
    const unsorted = [];
    prints.forEach((p) => {
        const s = resolveSeries(p);
        if (!s) {
            unsorted.push(p);
            return;
        }
        if (known.has(s)) known.get(s).push(p);
        else {
            if (!extras.has(s)) extras.set(s, []);
            extras.get(s).push(p);
        }
    });
    const orderedExtras = [...extras.entries()].sort((a, b) =>
        a[0].localeCompare(b[0]),
    );
    const groups = [];
    SERIES_ORDER.forEach((name) => {
        const items = known.get(name) || [];
        if (items.length > 0) groups.push({name, items: items.slice().sort(sortWithinSeries)});
    });
    orderedExtras.forEach(([name, items]) => {
        groups.push({name, items: items.slice().sort(sortWithinSeries)});
    });
    if (unsorted.length > 0) groups.push({name: "Unsorted", items: unsorted.slice().sort(sortWithinSeries)});
    return groups;
};

// ═══════════════════════════════════════════════════════════════════════
// PrintCard — universal print tile used across Featured + Series + In Home
// ═══════════════════════════════════════════════════════════════════════
//
// Phase 8 print-card refinement (per sign-off):
//   - Artwork at natural aspect ratio (uses EditorialImage `natural` mode)
//   - Title, series label, edition, starting price
//   - Single uniform CTA: "VIEW PRINT →" text-link + arrow icon below the
//     metadata block. Whole card remains clickable (luxury-photo-site
//     feel). The text-link gives explicit affordance for screen readers
//     and a clearer hover target.
//
// Click target preserved from Phase 7:
//   - travel-archive prints (`location` present) → /inquire?type=Fine-Art%20Prints
//   - shop prints (no location)                   → /atelier/[id] detail
const PrintCard = ({print}) => {
    const isTravel = Boolean(print.location);
    const to = isTravel
        ? `/inquire?type=Fine-Art%20Prints`
        : `/atelier/${print.id}`;
    const subline = isTravel
        ? `${print.location} · ${print.edition}`
        : `${print.collection} · ${print.edition}`;

    return (
        <article
            className="group"
            data-testid={`print-card-${print.id}`}
        >
            <Link to={to} className="block">
                <EditorialImage
                    src={print.image}
                    alt={print.title}
                    natural
                />
            </Link>
            <div className="mt-3 sm:mt-4">
                <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                        <Link
                            to={to}
                            className="font-serif-display text-[17px] sm:text-lg leading-snug hover:underline decoration-[hsl(var(--lux-brass))]"
                        >
                            {print.title}
                        </Link>
                        <div className="kicker mt-1 truncate">{subline}</div>
                    </div>
                    {print.startingAt ? (
                        <div className="text-[10px] sm:text-[11px] text-muted-foreground tracking-[0.14em] uppercase whitespace-nowrap pt-1">
                            From {print.startingAt}
                        </div>
                    ) : null}
                </div>
                <Link
                    to={to}
                    data-testid={`print-card-cta-${print.id}`}
                    className="inline-flex items-center mt-3 text-[10px] sm:text-[11px] tracking-[0.18em] uppercase text-foreground/80 hover:text-foreground transition-colors"
                >
                    View print
                    <span aria-hidden className="ml-1.5 transition-transform group-hover:translate-x-0.5">→</span>
                </Link>
            </div>
        </article>
    );
};

// ═══════════════════════════════════════════════════════════════════════
// InHomeCard — print + interior-mockup pair for the "In Home" section.
// ═══════════════════════════════════════════════════════════════════════
//
// Main visual: the interior shot showing the print mounted in a real room.
// Inset (bottom-right, ~96px square, white border + soft shadow): the
// artwork thumbnail itself, so the viewer can immediately associate the
// physical print with the room context.
const InHomeCard = ({print}) => {
    const isTravel = Boolean(print.location);
    const to = isTravel
        ? `/inquire?type=Fine-Art%20Prints`
        : `/atelier/${print.id}`;
    const subline = isTravel
        ? `${print.series || print.collection} · ${print.location}`
        : `${print.series || print.collection}`;

    return (
        <article
            className="group"
            data-testid={`in-home-card-${print.id}`}
        >
            <Link to={to} className="block relative">
                <EditorialImage
                    src={print.roomPreviewImage}
                    alt={print.roomPreviewAlt || `${print.title} in a room`}
                    natural
                />
                {/* Inset artwork thumbnail — bottom-right corner */}
                <div
                    className="absolute bottom-3 right-3 sm:bottom-4 sm:right-4 w-20 h-20 sm:w-24 sm:h-24 rounded-[2px] overflow-hidden bg-white p-1 shadow-[0_4px_18px_-4px_rgba(0,0,0,0.45)] ring-1 ring-black/10"
                    aria-hidden
                >
                    <img
                        src={print.image}
                        alt=""
                        className="block w-full h-full object-cover rounded-[1px]"
                        loading="lazy"
                        decoding="async"
                    />
                </div>
            </Link>
            <div className="mt-3 sm:mt-4">
                <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                        <Link
                            to={to}
                            className="font-serif-display text-[17px] sm:text-lg leading-snug hover:underline decoration-[hsl(var(--lux-brass))]"
                        >
                            {print.title}
                        </Link>
                        <div className="kicker mt-1 truncate">{subline}</div>
                    </div>
                    {print.startingAt ? (
                        <div className="text-[10px] sm:text-[11px] text-muted-foreground tracking-[0.14em] uppercase whitespace-nowrap pt-1">
                            From {print.startingAt}
                        </div>
                    ) : null}
                </div>
            </div>
        </article>
    );
};

// ═══════════════════════════════════════════════════════════════════════
// SeriesSection — labeled "room" containing one series' prints.
// ═══════════════════════════════════════════════════════════════════════
const SeriesSection = ({name, items}) => {
    const slug = slugifySeries(name);
    return (
        <section
            id={`series-${slug}`}
            data-testid={`atelier-series-${slug}`}
            className="scroll-mt-24 border-t border-border"
        >
            <Section spacing="normal">
                <div className="mb-8 sm:mb-10">
                    <Kicker>The Atelier · {name}</Kicker>
                    <SerifTitle className="mt-3">{name}.</SerifTitle>
                </div>
                <Masonry
                    items={items}
                    breakpoints={{base: 1, sm: 2, lg: 3}}
                    gap="gap-6 sm:gap-8"
                    testId={`series-grid-${slug}`}
                    renderItem={(print) => (
                        <PrintCard key={print.id} print={print} />
                    )}
                />
            </Section>
        </section>
    );
};

// ═══════════════════════════════════════════════════════════════════════
// PrintsPage — top-level component
// ═══════════════════════════════════════════════════════════════════════
//
// Section order (Phase 8 sign-off):
//   1. Hero            — atelier-hero heroMedia; hidden if not authored yet
//   2. Featured        — prints with featured===true; hidden if zero
//   3. Sticky Nav      — chip per series; hidden if zero series
//   4. Series ×7       — Phase 7 grouping (Japan → Quiet Human Presence)
//   5. In Home         — prints with roomPreviewImage; hidden if zero
//   6. Custom Wall Art — repositioned below In Home, reframed as service
//   7. Investment      — unchanged
//
// Hero copy block: hardcoded here (not pulled from siteSettings) — it's
// atelier-page-specific editorial copy. If this ever needs to be Sanity-
// editable, move to a `pageContent` doc type or add atelierHeroHeadline /
// atelierHeroBody fields to siteSettings. Not a Phase 8 task — flagged
// for whoever migrates next.
export default function Prints() {
    const finePrints = useFineArtPrints();
    const travelPrints = useTravelArchive();
    const hero = useAtelierHero();

    // Combined dataset, memoized so children don't re-render unnecessarily.
    const allPrints = useMemo(
        () => [...(finePrints || []), ...(travelPrints || [])],
        [finePrints, travelPrints],
    );

    // Phase 8 — derived subsets for the new sections.
    const featuredPrints = useMemo(
        () => allPrints.filter((p) => p.featured === true).sort(sortWithinSeries),
        [allPrints],
    );
    const inHomePrints = useMemo(
        () => allPrints.filter((p) => !!p.roomPreviewImage).sort(sortWithinSeries),
        [allPrints],
    );
    const groups = useMemo(() => groupBySeries(allPrints), [allPrints]);

    // Sticky nav sections — only those that actually have prints.
    const navSections = useMemo(
        () => groups.map((g) => ({slug: slugifySeries(g.name), label: g.name})),
        [groups],
    );

    return (
        <div className="pt-24 sm:pt-28" data-testid="atelier-page">
            <Seo {...SEO.atelier} />

            {/* ════════ 1. HERO ════════
              * Auto-hides if the atelier-hero doc isn't authored yet
              * (transition window — user populates progressively).
              */}
            {hero ? (
                <section data-testid="atelier-hero">
                    <div className="mx-auto w-full max-w-[1440px] px-4 sm:px-6 lg:px-10">
                        <div className="mt-2 sm:mt-4">
                            <EditorialImage
                                src={hero.desktop}
                                srcSet={hero.srcSet}
                                alt={hero.alt}
                                natural
                                loading="eager"
                                className="rounded-[2px]"
                            />
                        </div>
                        <div className="mt-10 sm:mt-14 max-w-[760px]">
                            <Kicker>The Atelier</Kicker>
                            <SerifTitle className="mt-3">
                                Photographs to live with.
                            </SerifTitle>
                            <p className="mt-5 max-w-[60ch] text-sm leading-[1.85] text-muted-foreground">
                                A small, collectible body of work &mdash; limited and
                                open editions, printed on archival fine&#8209;art papers
                                and finished by hand. Each piece is offered
                                inquiry&#8209;first: size, paper, and framing are
                                tailored after a brief conversation about the room
                                and the light it will live in.
                            </p>
                            <div className="mt-7 flex flex-wrap items-center gap-3">
                                <a
                                    href="#featured-prints"
                                    data-testid="hero-cta-featured"
                                    className="btn-request btn-request--sm"
                                >
                                    Shop Featured Prints
                                </a>
                                <a
                                    href="#custom-wall-art"
                                    data-testid="hero-cta-consultation"
                                    className="btn-request btn-request--sm btn-request--ghost"
                                >
                                    Request Wall Consultation
                                </a>
                            </div>
                        </div>
                    </div>
                </section>
            ) : (
                // No hero yet — show a minimal intro so the page never opens empty.
                <Section spacing="tight">
                    <Kicker>The Atelier</Kicker>
                    <SerifTitle className="mt-3">
                        Photographs to live with.
                    </SerifTitle>
                    <p className="mt-5 max-w-[60ch] text-sm leading-[1.85] text-muted-foreground">
                        A small, collectible body of work &mdash; limited and open
                        editions, printed on archival fine&#8209;art papers and
                        finished by hand. Each piece is offered inquiry&#8209;first:
                        size, paper, and framing are tailored after a brief
                        conversation about the room and the light it will live in.
                    </p>
                    <div className="mt-7 flex flex-wrap items-center gap-3">
                        <a href="#featured-prints" className="btn-request btn-request--sm">
                            Shop Featured Prints
                        </a>
                        <a href="#custom-wall-art" className="btn-request btn-request--sm btn-request--ghost">
                            Request Wall Consultation
                        </a>
                    </div>
                </Section>
            )}

            {/* ════════ 2. FEATURED PRINTS ════════
              * Auto-hides if zero prints have featured===true. User is
              * expected to mark 4-6 strongest pieces. Same masonry +
              * print-card pattern as the series sections below.
              */}
            {featuredPrints.length > 0 && (
                <section
                    id="featured-prints"
                    data-testid="atelier-featured"
                    className="scroll-mt-24 border-t border-border"
                >
                    <Section spacing="normal">
                        <div className="mb-8 sm:mb-10">
                            <Kicker>Featured · Collector Pieces</Kicker>
                            <SerifTitle className="mt-3">Featured prints.</SerifTitle>
                        </div>
                        <Masonry
                            items={featuredPrints}
                            breakpoints={{base: 1, sm: 2, lg: 3}}
                            gap="gap-6 sm:gap-8"
                            testId="featured-grid"
                            renderItem={(print) => (
                                <PrintCard key={print.id} print={print} />
                            )}
                        />
                    </Section>
                </section>
            )}

            {/* ════════ 3. STICKY THEME NAV ════════
              * Chips per available series. Active state via IO scroll-spy.
              * Renders nothing if zero series. Anchor IDs match the
              * SeriesSection ids below (`series-<slug>`).
              */}
            {navSections.length > 0 && (
                <div className="border-t border-border">
                    <div className="mx-auto w-full max-w-[1440px] px-4 sm:px-6 lg:px-10">
                        <StickyThemeNav sections={navSections} />
                    </div>
                </div>
            )}

            {/* ════════ 4. SERIES SECTIONS ════════ */}
            {groups.map((g) => (
                <SeriesSection key={g.name} name={g.name} items={g.items} />
            ))}

            {/* ════════ 5. IN HOME ════════
              * Auto-hides if zero prints have roomPreviewImage uploaded.
              * Each card pairs the room mockup with a small artwork inset.
              */}
            {inHomePrints.length > 0 && (
                <section
                    id="in-home"
                    data-testid="atelier-in-home"
                    className="scroll-mt-24 border-t border-border"
                >
                    <Section spacing="normal">
                        <div className="mb-8 sm:mb-10">
                            <Kicker>In Home · Room Context</Kicker>
                            <SerifTitle className="mt-3">Imagine yours.</SerifTitle>
                            <p className="mt-5 max-w-[60ch] text-sm leading-[1.85] text-muted-foreground">
                                A few collector pieces, mounted in real rooms. See how
                                the print lives at scale, against architecture, in
                                conversation with the light it was made for.
                            </p>
                        </div>
                        <Masonry
                            items={inHomePrints}
                            breakpoints={{base: 1, sm: 2, lg: 2}}
                            gap="gap-6 sm:gap-10"
                            testId="in-home-grid"
                            renderItem={(print) => (
                                <InHomeCard key={print.id} print={print} />
                            )}
                        />
                    </Section>
                </section>
            )}

            {/* ════════ 6. CUSTOM WALL ART ════════
              * Repositioned below In Home per Phase 8 spec. Copy reframed
              * to feel like a service layer (sizing, framing, room
              * placement) rather than just an inquiry CTA. The form/CTA
              * itself is unchanged.
              */}
            <section
                id="custom-wall-art"
                data-testid="section-custom-wall-art"
                className="scroll-mt-24 border-t border-border"
            >
                <Section spacing="normal" className="text-center">
                    <Kicker>The Atelier · Custom Wall Art</Kicker>
                    <SerifTitle className="mt-3 max-w-[26ch] mx-auto">
                        Sized, framed, and placed for your room.
                    </SerifTitle>
                    <p className="mt-5 text-sm text-muted-foreground max-w-[60ch] mx-auto leading-[1.85]">
                        A consultation service for collectors who want a print
                        sized to a specific wall, finished in a frame chosen for
                        the architecture, and installed at a scale that holds the
                        room. Light, proportion, and the print&apos;s natural
                        gravity all considered before anything is committed.
                    </p>
                    <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
                        <Link
                            to="/inquire?type=Fine-Art%20Prints"
                            data-testid="custom-wall-art-primary-cta"
                            className="btn-request"
                        >
                            Request a Custom Wall Art Consultation
                        </Link>
                    </div>
                    <Separator className="my-10 bg-border max-w-[12rem] mx-auto" />
                    <p className="text-xs text-muted-foreground">
                        All editions are signed and accompanied by an archival
                        certificate.
                    </p>
                </Section>
            </section>

            {/* ════════ 7. ATELIER INVESTMENT TIERS ════════ */}
            <section className="py-14 sm:py-20 lg:py-28 border-t border-border bg-[hsl(var(--lux-porcelain))]">
                <div className="mx-auto w-full max-w-[1200px] px-4 sm:px-6 lg:px-10">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end mb-12">
                        <div className="lg:col-span-7">
                            <span className="kicker">The Atelier · Investment</span>
                            <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl mt-3 leading-[1.05]">
                                Four ways to collect.
                            </h2>
                        </div>
                        <p className="lg:col-span-5 text-sm leading-[1.85] text-muted-foreground max-w-[48ch]">
                            From single prints to custom wall art consultations &mdash;
                            every piece is signed and archival, designed for the room
                            it will live in.
                        </p>
                    </div>
                    <div
                        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5"
                        data-testid="atelier-tiers"
                    >
                        {ATELIER_TIERS.map((t) => (
                            <article
                                key={t.id}
                                data-testid={`atelier-tier-${t.id}`}
                                className="bg-background border border-border rounded-[3px] p-6 sm:p-7 flex flex-col h-full"
                            >
                                <h3 className="font-serif-display text-xl leading-tight">{t.name}</h3>
                                <div className="mt-3 text-[11px] tracking-[0.16em] uppercase text-[hsl(var(--lux-oxblood))]">
                                    {t.starting}
                                </div>
                                <Separator className="my-4 bg-[hsl(var(--lux-brass))]/45 w-8" />
                                <p className="text-[13px] leading-[1.7] text-muted-foreground">
                                    {t.note}
                                </p>
                            </article>
                        ))}
                    </div>
                    <div className="mt-10 flex flex-wrap justify-center gap-3">
                        <Link
                            to="/inquire?type=Fine-Art%20Prints"
                            className="btn-request btn-request--sm btn-request--ghost"
                        >
                            Request a Print Consultation
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
